import { useEffect, useRef, useCallback, useState } from 'react';
import { BASE_URL } from '../api/client';

const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];

/**
 * Server-Sent Events hook.
 *
 * SSE doesn't support custom headers, so we pass the JWT as ?token=
 *
 * @param {function} onDetection  - called with detection event data
 * @param {function} onDeviceStatus - called with device_status event data
 * @param {function} onHealth    - called with health event data
 * @param {function} onActivityStatus - called with activity_status event data
 *   (manual screenshot/browser-history job progress — see
 *   api/activity.py's broadcaster.broadcast("activity_status", ...) calls
 *   and main.py's activity-job reaper)
 * @returns {{ connected: bool, reconnecting: bool }}
 */
export function useSSE(onDetection, onDeviceStatus, onHealth, onActivityStatus) {
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const esRef = useRef(null);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const url = `${BASE_URL}/api/v1/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      setReconnecting(false);
      attemptRef.current = 0;
    };

    es.addEventListener('detection', (e) => {
      try {
        onDetection && onDetection(JSON.parse(e.data));
      } catch (_) {}
    });

    es.addEventListener('device_status', (e) => {
      try {
        onDeviceStatus && onDeviceStatus(JSON.parse(e.data));
      } catch (_) {}
    });

    es.addEventListener('health', (e) => {
      try {
        onHealth && onHealth(JSON.parse(e.data));
      } catch (_) {}
    });

    es.addEventListener('activity_status', (e) => {
      try {
        onActivityStatus && onActivityStatus(JSON.parse(e.data));
      } catch (_) {}
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setConnected(false);
      setReconnecting(true);

      const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
      attemptRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };
  }, [onDetection, onDeviceStatus, onHealth, onActivityStatus]);

  useEffect(() => {
    connect();
    return () => {
      if (esRef.current) esRef.current.close();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [connect]);

  return { connected, reconnecting };
}
